import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Pencil, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { MultiButton, MultiButtonGroup } from '@/components/ui/multi-button'
import { AdminListPage } from '../components/AdminListPage'
import { useAdminFestivals } from '../hooks/useAdminFestivals'
import type { FestivalDocument } from '@/types'

export function AdminFestivalsPage() {
  const navigate = useNavigate()
  const { festivals, loading, error, deleteFestival } = useAdminFestivals()
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<(FestivalDocument & { id: string }) | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)

  async function handleDelete() {
    if (!deleteTarget) return
    setDeletingId(deleteTarget.id)
    try {
      await deleteFestival(deleteTarget.id)
    } finally {
      setDeletingId(null)
      setDeleteTarget(null)
      setDialogOpen(false)
    }
  }

  function openDeleteDialog(festival: FestivalDocument & { id: string }) {
    setDeleteTarget(festival)
    setDialogOpen(true)
  }

  return (
    <AdminListPage
      title="Festivals"
      action={
        <Button asChild>
          <Link to="/admin/config/festivals/new">Add Festival</Link>
        </Button>
      }
      error={error}
      loading={loading}
      skeletonRows={3}
      dialog={
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger className="hidden" />
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Festival</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              Are you sure you want to delete{' '}
              <span className="font-semibold text-foreground">{deleteTarget?.name}</span>? This will
              not delete the edition subcollections and cannot be undone.
            </p>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => { setDialogOpen(false); setDeleteTarget(null) }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => void handleDelete()}
                disabled={deletingId !== null}
              >
                {deletingId !== null ? 'Deleting…' : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      }
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Slug</TableHead>
            <TableHead>Tagline</TableHead>
            <TableHead>Published</TableHead>
            <TableHead>Order</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <MultiButtonGroup>
          <TableBody>
            {festivals.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                  No festivals yet. Add one to get started.
                </TableCell>
              </TableRow>
            ) : (
              festivals.map((festival) => (
                <TableRow key={festival.id}>
                  <TableCell className="font-medium">{festival.name}</TableCell>
                  <TableCell className="text-muted-foreground">{festival.slug}</TableCell>
                  <TableCell className="max-w-xs truncate text-muted-foreground">
                    {festival.tagline}
                  </TableCell>
                  <TableCell>
                    <Badge variant={festival.isPublished ? 'default' : 'secondary'}>
                      {festival.isPublished ? 'Published' : 'Draft'}
                    </Badge>
                  </TableCell>
                  <TableCell>{festival.order}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end">
                      <MultiButton
                        variant="outline"
                        size="sm"
                        items={[
                          {
                            id: 'edit',
                            icon: Pencil,
                            label: 'Edit',
                            onClick: () => navigate(`/admin/config/festivals/${festival.id}/edit`),
                          },
                          {
                            id: 'delete',
                            icon: Trash2,
                            label: 'Delete',
                            onClick: () => openDeleteDialog(festival),
                            disabled: deletingId === festival.id,
                            className: 'text-destructive hover:text-destructive',
                          },
                        ]}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </MultiButtonGroup>
      </Table>
    </AdminListPage>
  )
}
